// Import trimestriel de Kohesio (opérations de la politique de cohésion de l'UE, CC BY 4.0) : le serveur de Kohesio
// refuse l'adresse du serveur du radar (403, constaté le 21/10/2026), le responsable des imports télécharge lui-même
// les exports par pays (un CSV par pays, environ 660 Mo en tout) et les choisit ici, tous ensemble. Le navigateur lit
// chaque fichier en flux et n'envoie que les grosses opérations en euros, réduites aux colonnes que lit le worker
// (apps/worker/src/radar/connectors/kohesio.py, `select`), qui applique ensuite toutes les règles de la source.

export const KOHESIO_DATA = "https://kohesio.ec.europa.eu/en/data";
const DUE_DAY = 6;
// Seuil de dépense éligible du navigateur : égal à `min_amount_eur` de sources.toml (jamais au-dessus).
const MIN_AMOUNT_EUR = 10_000_000;

// Colonnes lues par `kohesio.select` ; aucune autre n'est envoyée.
export const KOHESIO_FIELDS = [
  "Operation_Unique_Identifier",
  "Operation_Local_Identifier",
  "Operation_Name_English",
  "Operation_Name_Programme_Language",
  "Operation_Start_Date",
  "Operation_End_Date",
  "Total_Eligible_Expenditure_amount",
  "Total_Eligible_Expenditure_Currency",
  "Project_EU_Budget",
  "Beneficiary_Name",
  "Category_Of_Intervention",
  "Category_Label",
  "Fund_Name",
  "Programme_Name",
  "LAU_Labels",
  "NUTS3_Label",
  "Operation_Summary_English",
  "CountryCode",
] as const;

export type KohesioRecord = { export: string; row: Record<string, string> };

/** Pays et date du lot d'un export par pays (« CZ-pp21-27-20260924.csv », précédé de la date du lot quand il est
 * téléchargé depuis la page des données : « 2026-09-24_CZ-pp21-27-20260924.csv ») ; undefined pour un autre fichier. */
export const exportFromFileName = (
  name: string,
): { country: string; export: string } | undefined => {
  const match =
    /^(?:\d{4}-\d{2}-\d{2}_)?([A-Z]{2})-pp21-27-(\d{4})(\d{2})(\d{2})(?: \(\d+\))?\.csv$/i.exec(
      name.trim(),
    );
  if (!match) return undefined;
  return {
    country: match[1].toUpperCase(),
    export: `${match[2]}-${match[3]}-${match[4]}`,
  };
};

/** Trimestre attendu (« 2026-10 »), à partir du 6 de janvier, avril, juillet ou octobre : même règle que le worker. */
export const quarterPeriod = (today: Date) => {
  let year = today.getFullYear();
  let month = Math.floor(today.getMonth() / 3) * 3 + 1;
  if (today.getMonth() + 1 === month && today.getDate() < DUE_DAY) {
    month -= 3;
    if (month < 1) {
      month += 12;
      year -= 1;
    }
  }
  return `${year}-${String(month).padStart(2, "0")}`;
};

/** Lecteur CSV en flux (virgule, guillemets doublés, sauts de ligne dans les champs entre guillemets). */
export class CsvReader {
  private field = "";
  private row: string[] = [];
  private quoted = false;
  // Guillemet lu dans un champ entre guillemets : fin du champ, ou premier d'un guillemet doublé.
  private quote = false;

  push(text: string): string[][] {
    const rows: string[][] = [];
    for (const char of text) {
      if (this.quoted) {
        if (this.quote) {
          this.quote = false;
          if (char === '"') {
            this.field += '"';
            continue;
          }
          this.quoted = false;
        } else if (char === '"') {
          this.quote = true;
          continue;
        } else {
          this.field += char;
          continue;
        }
      }
      if (char === '"' && this.field === "") this.quoted = true;
      else if (char === ",") {
        this.row.push(this.field);
        this.field = "";
      } else if (char === "\n") {
        this.row.push(
          this.field.endsWith("\r") ? this.field.slice(0, -1) : this.field,
        );
        rows.push(this.row);
        this.row = [];
        this.field = "";
      } else this.field += char;
    }
    return rows;
  }

  flush(): string[][] {
    if (this.quote) {
      this.quote = false;
      this.quoted = false;
    }
    if (this.field === "" && this.row.length === 0) return [];
    this.row.push(this.field);
    const row = this.row;
    this.row = [];
    this.field = "";
    return [row];
  }
}

/** Opération réduite aux colonnes lues, si sa dépense éligible en euros atteint le seuil ; undefined sinon. */
export const compactOperation = (
  header: string[],
  fields: string[],
): Record<string, string> | undefined => {
  const value = (name: string) => fields[header.indexOf(name)] ?? "";
  const amount = Number(value("Total_Eligible_Expenditure_amount") || NaN);
  if (!(amount >= MIN_AMOUNT_EUR)) return undefined;
  if (value("Total_Eligible_Expenditure_Currency") !== "EUR") return undefined;
  const row: Record<string, string> = {};
  for (const name of KOHESIO_FIELDS) {
    const text = value(name);
    if (text) row[name] = text;
  }
  return row;
};

export type KohesioProgress = {
  bytesRead: number;
  totalBytes: number;
  operations: number;
  kept: number;
};

/** Lit les exports choisis (tous du même lot) et renvoie les opérations retenues. */
export const readKohesioFiles = async (
  files: File[],
  onProgress: (progress: KohesioProgress) => void,
): Promise<{ export: string; records: KohesioRecord[] }> => {
  const exports = new Set<string>();
  for (const file of files) {
    const parsed = exportFromFileName(file.name);
    if (!parsed) throw new Error(`bad_name:${file.name}`);
    exports.add(parsed.export);
  }
  if (exports.size !== 1) throw new Error("mixed_exports");
  const [exportDate] = [...exports];
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const records: KohesioRecord[] = [];
  let bytesRead = 0;
  let operations = 0;
  for (const file of files) {
    const reader = new CsvReader();
    const decoder = new TextDecoder();
    let header: string[] | undefined;
    const take = (rows: string[][]) => {
      for (const fields of rows) {
        if (!header) {
          header = fields.map((name) => name.replace(/^\uFEFF/, ""));
          if (!header.includes("Total_Eligible_Expenditure_amount"))
            throw new Error(`bad_header:${file.name}`);
          continue;
        }
        operations += 1;
        const row = compactOperation(header, fields);
        if (row) records.push({ export: exportDate, row });
      }
    };
    const stream = file.stream().getReader();
    for (;;) {
      const { done, value } = await stream.read();
      if (done) break;
      take(reader.push(decoder.decode(value, { stream: true })));
      bytesRead += value.length;
      onProgress({ bytesRead, totalBytes, operations, kept: records.length });
    }
    take(reader.push(decoder.decode()));
    take(reader.flush());
  }
  onProgress({ bytesRead, totalBytes, operations, kept: records.length });
  return { export: exportDate, records };
};
